/**
 * useTicketDetail Hook
 */

import { useCallback, useEffect, useState } from 'react';
import { addTicketReply, getTicketDetail, rateTicket } from '../api';
import type { SupportTicket, TicketReply } from '../types';

interface UseTicketDetailOptions {
  ticketId: string;
}

interface UseTicketDetailReturn {
  ticket: SupportTicket | null;
  replies: TicketReply[];
  isLoading: boolean;
  isSending: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  sendReply: (content: string) => Promise<boolean>;
  submitRating: (rating: number) => Promise<boolean>;
}

export function useTicketDetail({ ticketId }: UseTicketDetailOptions): UseTicketDetailReturn {
  const [ticket, setTicket] = useState<SupportTicket | null>(null);
  const [replies, setReplies] = useState<TicketReply[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDetail = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await getTicketDetail(ticketId);

      if (!response.success) {
        setError(response.error || 'Failed to load ticket');
        return;
      }

      setTicket(response.ticket);
      setReplies(response.replies);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load ticket');
    } finally {
      setIsLoading(false);
    }
  }, [ticketId]);

  const sendReply = useCallback(async (content: string): Promise<boolean> => {
    setIsSending(true);
    setError(null);

    try {
      const response = await addTicketReply(ticketId, content);

      if (!response.success) {
        setError(response.error || 'Failed to send reply');
        return false;
      }

      // Refresh to get updated replies
      await fetchDetail();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reply');
      return false;
    } finally {
      setIsSending(false);
    }
  }, [ticketId, fetchDetail]);

  const submitRating = useCallback(async (rating: number): Promise<boolean> => {
    try {
      const response = await rateTicket(ticketId, rating);

      if (!response.success) {
        setError(response.error || 'Failed to submit rating');
        return false;
      }

      // Update local state
      setTicket(prev => prev ? { ...prev, satisfaction_rating: rating } : null);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit rating');
      return false;
    }
  }, [ticketId]);

  // Initial fetch
  useEffect(() => {
    if (ticketId) {
      fetchDetail();
    }
  }, [ticketId, fetchDetail]);

  return {
    ticket,
    replies,
    isLoading,
    isSending,
    error,
    refresh: fetchDetail,
    sendReply,
    submitRating,
  };
}
